import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MeUser } from 'src/app/account/interfaces/me.interface';
import { SuccessResponse } from 'src/app/account/services/account-api.service';
import { Company } from 'src/app/core/models/user.model';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { environment } from 'src/environments/environment';

// Proper interfaces for authentication requests
export interface LoginRequest {
  username: string;
  password: string;
  provider: string;
  seller_company?: string;
}

export interface SignupRequest {
  username: string;
  password: string;
  email: string;
  first_name: string;
  last_name: string;
  provider: string;
  seller_company?: string;
}

export interface OtpRequest {
  username: string;
  provider: string;
  seller_company?: string;
}


export interface OtpRequestMobile {
  mobile_number: string;
  provider: string;
  seller_company?: string;
}


export interface OtpVerificationRequest {
  verification_token: string;
  mobile_number?: string;
  username?: string;
  provider?: string;
  seller_company?: string;
}

export interface ResetPasswordRequest {
  new_password: string;
}

export interface OtpSubmitResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user: MeUser;
  company: Company;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationApiService {
  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  private _url = signal<string>(environment.admin_url);
  readonly url = this._url.asReadonly();

  readonly environment = computed(() => this.environmentService.environment());

  /**
   * Login user with credentials
   */
  async login(reqObj: LoginRequest): Promise<LoginResponse> {
    try {
      const environment = this.environment();

      const requestBody = {
        ...reqObj,
        seller_company: environment?.sellerId ?? ''
      };

      return await firstValueFrom(
        this.http.post<LoginResponse>(`${this.url()}/admin/auth/login/`, requestBody)
      );
    } catch (error) {
      // Login failed
      throw error;
    }
  }

  /**
   * Send OTP to user
   */
  async sendOTP(reqObj: OtpRequest): Promise<SuccessResponse> {
    try {
      return await firstValueFrom(
        this.http.post<SuccessResponse>(`${this.url()}/admin/auth/request_login_otp/`, reqObj)
      );
    } catch (error) {
      // Send OTP failed
      throw error;
    }
  }



  async sendOTPToMobileNumber(reqObj: OtpRequestMobile): Promise<SuccessResponse> {
    try {
      return await firstValueFrom(
        this.http.post<SuccessResponse>(`${this.url()}/consumer/auth/request-otp/`, reqObj)
      );
    } catch (error) {
      // Send OTP failed
      throw error;
    }
  }

  /**
   * Verify OTP from user
   */
  async verifyOTP(reqObj: OtpVerificationRequest): Promise<LoginResponse> {
    try {
      const environment = this.environment();
      const requestBody: OtpVerificationRequest = {
        ...reqObj,
        seller_company: reqObj.seller_company ?? environment?.sellerId ?? ''
      };
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${this.url()}/auth/validate_login_otp/`, requestBody)
      );

      return response;
    } catch (error) {
      throw error;
    }
  }



  async verifyMobileNumberOTP(reqObj: OtpVerificationRequest): Promise<LoginResponse> {
    try {

      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${this.url()}/consumer/auth/validate-otp/`, reqObj)
      );

      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign up new user
   */
  async signup(reqObj: SignupRequest): Promise<LoginResponse> {
    try {
      const environment = this.environment();

      const requestBody = {
        ...reqObj,
        seller_company: environment?.sellerId ?? ''
      };

      return await firstValueFrom(
        this.http.post<LoginResponse>(`${this.url()}/consumer/auth/signup/`, requestBody)
      );
    } catch (error) {
      // Signup failed
      throw error;
    }
  }

  /**
   * Send reset password OTP
   */
  async sendResetPasswordOTP(reqObj: OtpRequest): Promise<SuccessResponse> {
    try {
      return await firstValueFrom(
        this.http.post<SuccessResponse>(`${this.url()}/consumer/auth/request_reset_password_otp/`, reqObj)
      );
    } catch (error) {
      // Send reset password OTP failed
      throw error;
    }
  }

  /**
   * Verify reset password OTP
   */
  async verifyResetPasswordOTP(reqObj: OtpVerificationRequest): Promise<OtpSubmitResponse> {
    try {
      return await firstValueFrom(
        this.http.post<OtpSubmitResponse>(`${this.url()}/consumer/auth/validate_reset_password_otp/`, reqObj)
      );
    } catch (error) {
      // Verify reset password OTP failed
      throw error;
    }
  }

  /**
   * Reset user password
   */
  async resetPassword(reqObj: ResetPasswordRequest): Promise<SuccessResponse> {
    try {
      return await firstValueFrom(
        this.http.post<SuccessResponse>(`${this.url()}/consumer/auth/reset_password/`, reqObj)
      );
    } catch (error) {
      // Reset password failed
      throw error;
    }
  }
}
